'use client'

import { useEffect, useRef } from 'react'
import { useUserContext } from '@/contexts/UserContext'
import { getCookie } from 'cookies-next'
import { trackEvent } from '@nile/mantine-theme/analytics'

type SignupTrackerProps = {
    onComplete?: () => void
}

export default function SignupTracker({ onComplete }: SignupTrackerProps) {
    const { fetchUser } = useUserContext()
    const trackingEventsFired = useRef(false)

    useEffect(() => {
        const alreadyTracked = localStorage.getItem('signup_complete_tracked')
        if (!trackingEventsFired.current && !alreadyTracked) {
            const sendTrackingEvents = async () => {
                const ANALYTICS_TIMEOUT = 4000

                try {
                    const user = await fetchUser()
                    if (!user) return

                    const utmCampaign = getCookie('utm_campaign', {}) || ''
                    const utmContent = getCookie('utm_content', {}) || ''
                    const utmMedium = getCookie('utm_medium', {}) || ''
                    const utmSource = getCookie('utm_source', {}) || ''
                    const gclid = getCookie('gclid', {}) || ''
                    const gbraid = getCookie('gbraid', {}) || ''
                    const wbraid = getCookie('wbraid', {}) || ''
                    const fbclid = getCookie('fbclid', {}) || ''
                    const msclkid = getCookie('msclkid', {}) || ''
                    const kadCid = getCookie('kad_cid', {}) || ''
                    const oppref = getCookie('oppref', {}) || ''
                    const inAppBrowser = getCookie('in_app_browser', {}) || ''

                    const analyticsPromise = async () => {
                        const promises = []

                        if (window.analytics) {
                            promises.push(
                                trackEvent('signup_complete', {
                                    email: user.email,
                                    phone: user.phoneNumber,
                                    gclid,
                                    gbraid,
                                    wbraid,
                                    fbclid,
                                    msclkid,
                                    kad_cid: kadCid,
                                    oppref,
                                    in_app_browser: inAppBrowser,
                                }),
                                window.analytics.alias(user.id),
                                window.analytics.identify(user.id, {
                                    email: user.email,
                                    id: user.id,
                                    phone: user.phoneNumber,
                                    utmCampaign,
                                    utmContent,
                                    utmMedium,
                                    utmSource,
                                    gclid,
                                    gbraid,
                                    wbraid,
                                    fbclid,
                                    msclkid,
                                    kadCid,
                                    oppref,
                                    inAppBrowser,
                                }),
                                window.analytics.track('merge_identity', {}),
                            )
                        }

                        window.localStorage.setItem('patientId', user.id || '')

                        await Promise.allSettled(promises)
                    }

                    await Promise.race([
                        analyticsPromise(),
                        new Promise((resolve) => setTimeout(resolve, ANALYTICS_TIMEOUT)),
                    ])

                    trackingEventsFired.current = true
                    localStorage.setItem('signup_complete_tracked', 'true')
                } catch (error) {
                    console.error('Analytics failed', error)
                } finally {
                    onComplete?.()
                }
            } 

            sendTrackingEvents()
        }
    }, [fetchUser, onComplete])

    return null
}
